import { useMutation, useQueries, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { IUserMe, IUserPublic } from '~/types/user'
import type { IUserPreferences } from '~/types/user-preference'

import { getConnectedUsersCount, getMeAsUser, getUser, getUsers } from '~/api/authentication'
import { archiveUser, unarchiveUser } from '~/api/mwm-users'
import { getUserPreferences, updateUserPreferences } from '~/api/user-preferences'

type SelectCallback<T> = (user: IUserPublic) => T

////////// KEYS //////////

export const usersKeys = {
  all: ['users'] as const,
  count: () => [...usersKeys.all, 'count'] as const,
  details: (userId?: string) => [...usersKeys.all, 'details', userId] as const,
  list: () => [...usersKeys.all, 'list'] as const,
  preferences: () => [...usersKeys.all, 'preferences'] as const,
  profile: () => [...usersKeys.all, 'profile'] as const,
}

////////// QUERIES //////////

export const useUserPreferencesQuery = (options?: { enabled?: boolean }): UseQueryResult<IUserPreferences> => {
  return useQuery({ queryFn: getUserPreferences, queryKey: usersKeys.preferences(), ...options })
}

export const useGetMeAsUserQuery = (enabled?: boolean): UseQueryResult<IUserMe> => {
  return useQuery({ enabled, queryFn: getMeAsUser, queryKey: usersKeys.profile(), refetchOnMount: false })
}

export const useConnectedUsersCountQuery = (): UseQueryResult<{ count: number }> => {
  return useQuery({ queryFn: getConnectedUsersCount, queryKey: usersKeys.count(), refetchInterval: 10000 })
}

export const useUserDetailsQuery = <T = IUserPublic>(
  userId?: string,
  enabled?: boolean,
  select?: SelectCallback<T>
): UseQueryResult<T> => {
  return useQuery({
    enabled: !!userId && enabled,
    queryFn: () => getUser(userId as string),
    queryKey: usersKeys.details(userId as string),
    select,
  })
}

export const useUsersListQuery = (): UseQueryResult<IUserPublic[]> => {
  return useQuery({ queryFn: getUsers, queryKey: usersKeys.list() })
}

////////// MULTI QUERIES //////////

export const useUserDetailsQueries = <T = IUserPublic>(
  userIds?: string[] | null,
  select?: SelectCallback<T>
): UseQueryResult<T | IUserPublic | undefined>[] => {
  const queries =
    !userIds || userIds.length === 0
      ? [
          {
            queryKey: usersKeys.details('no-data'),
            select,
          },
        ]
      : userIds.map((userId) => {
          return {
            enabled: !!userIds,
            queryFn: () => getUser(userId),
            queryKey: usersKeys.details(userId),
            select,
          }
        })
  return useQueries({ queries })
}

////////// MUTATIONS //////////

export const useUpdateUserPreferencesMutation = (): UseMutationResult<IUserPreferences, unknown, IUserPreferences> => {
  return useMutation({ mutationFn: updateUserPreferences })
}

export const useArchiveUserMutation = (): UseMutationResult<void, unknown, { id: string }> => {
  return useMutation({ mutationFn: archiveUser })
}

export const useUnarchiveUserMutation = (): UseMutationResult<void, unknown, { id: string }> => {
  return useMutation({ mutationFn: unarchiveUser })
}
